import { useRef, useState } from "react";
import { useParams } from "react-router";
import { SelectInstance } from "react-select";
import CreatableSelect from "react-select/creatable";
import { SyncDirection } from "../../../../../../../common/sesh_types";
import { DashboardUtils } from "../../../../dashboard_utils";
import { trpc } from "../../../../utils/trpc";

interface OptionType {
	value: string;
	label: React.ReactElement | string;
}

export default function GoogleCalendarPicker(props: {
	syncType: SyncDirection;
	hasUnsavedChanges: boolean;
	gcal_id: string | null;
	set_gcal_id: (gcal_id: string | null) => void;
	set_gcal_name: (gcal_name: string | null) => void;
}) {
	const { server_id } = useParams<{ server_id: string }>();

	const [selectedCalendar, setSelectedCalendar] = useState(null);

	const [shouldShowEnterNamePrompt, setShouldShowEnterNamePrompt] =
		useState(false);

	const selectRef = useRef<SelectInstance<OptionType> | null>(null);

	const calendarListQuery = trpc.gcal.calendarList.useQuery(
		{
			guildId: server_id,
			minAccessRole:
				props.syncType === SyncDirection.Inbound ? "reader" : "writer",
		},
		{
			onSuccess: (data) => {
				const calendars = data;

				const initialCalendar = props.gcal_id
					? calendars.find((calendar) => {
							return calendar.id === props.gcal_id;
					  })
					: null;

				if (initialCalendar) {
					setSelectedCalendar({
						value: initialCalendar.id,
						label: initialCalendar.summary,
					});
				}
			},
		}
	);

	const createCalendarMutation = trpc.gcal.createCalendar.useMutation();

	let directionText: string;
	let tipText: JSX.Element | string;
	switch (props.syncType) {
		case SyncDirection.Outbound:
			directionText = "Target";
			tipText = <>Which Google Calendar to send events to</>;
			break;
		case SyncDirection.Inbound:
			directionText = "Source";
			tipText = <>Which Google Calendar to sync with</>;
			break;
		case SyncDirection.Bidirectional:
			directionText = "Target/source";
			tipText = <>Which Google Calendar to sync with</>;
			break;
	}

	const header = DashboardUtils.CreateHeaderAndInfoButton(
		`${directionText} Google Calendar`,
		tipText,
		false,
		false,
		props.hasUnsavedChanges
	);

	if (calendarListQuery.isLoading) {
		return (
			<div>
				{header}{" "}
				<CreatableSelect
					classNamePrefix="sesh-dropdown"
					isLoading={true}
					loadingMessage={() => "Loading from Google Calendar..."}
				/>
			</div>
		);
	}

	if (!calendarListQuery.isSuccess) {
		return (
			<p className="has-text-danger">
				Error fetching Google Calendars (
				{calendarListQuery.error.message})
			</p>
		);
	}

	const hasSuccessfullyCreatedNewCalendar =
		createCalendarMutation.isSuccess &&
		createCalendarMutation.data.status === 200;

	const shouldAddCalendarFromCreateMutationToList =
		hasSuccessfullyCreatedNewCalendar &&
		!calendarListQuery.data.find(
			(calendar) => calendar.id === createCalendarMutation.data.data.id
		);

	const calendars = shouldAddCalendarFromCreateMutationToList
		? [...calendarListQuery.data, createCalendarMutation.data.data]
		: calendarListQuery.data;

	const options = [
		...calendars.map((calendar) => {
			return { value: calendar.id, label: calendar.summary };
		}),
		{
			value: "new",
			label: <span className="italic">Create new Google Calendar</span>,
		},
	];

	const handleCreate = (inputValue: string) => {
		props.set_gcal_id(null);
		props.set_gcal_name(null);

		setSelectedCalendar(null);
		createCalendarMutation.mutate(
			{
				gcal_name: inputValue,
				guild_id: server_id,
			},
			{
				onSuccess: (data) => {
					if (data.status !== 200) {
						console.log("Google Calendar API error", data);
						return; // TODO: handle error
					}
					const calendar = data.data;

					props.set_gcal_id(calendar.id);
					props.set_gcal_name(calendar.summary);
					setSelectedCalendar({
						value: calendar.id,
						label: calendar.summary,
					});
					setShouldShowEnterNamePrompt(false);
				},
			}
		);
	};

	return (
		<div>
			{header}{" "}
			<CreatableSelect
				ref={selectRef}
				classNamePrefix="sesh-dropdown"
				options={options}
				value={selectedCalendar}
				isDisabled={createCalendarMutation.isLoading}
				isLoading={createCalendarMutation.isLoading}
				onChange={(newValue) => {
					if (newValue.value == "new") {
						// hack to set focus properly
						// https://github.com/JedWatson/react-select/issues/3871#issuecomment-757648697
						selectRef.current?.blur();
						selectRef.current?.focus();

						props.set_gcal_id(null);
						props.set_gcal_name(null);
						setShouldShowEnterNamePrompt(true);
						setSelectedCalendar(null);
					} else {
						props.set_gcal_id(newValue.value);
						props.set_gcal_name(newValue.label);
						setSelectedCalendar(newValue);
						setShouldShowEnterNamePrompt(false);
					}
				}}
				formatCreateLabel={(inputValue) => {
					return (
						<span className="!italic">
							Create new Google Calendar named "
							<span className="!font-bold">{inputValue}</span>"
						</span>
					);
				}}
				onBlur={() => {
					if (shouldShowEnterNamePrompt) {
						setShouldShowEnterNamePrompt(false);
					}
				}}
				onCreateOption={handleCreate}
				placeholder={
					createCalendarMutation.isLoading
						? "Creating new Google Calendar..."
						: shouldShowEnterNamePrompt
						? "Enter name for new Google Calendar..."
						: "Select..."
				}
			/>
		</div>
	);
}
